import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { MessageSquare, Plus, Settings2, UserPlus } from 'lucide-react';
import { useMemo } from 'react';
import {
  availabilityQueryOptions,
  peopleAnalysisQueryOptions,
  todayAnalysisQueryOptions,
  type TodayPerson,
} from '~/api/analysis';
import { useMe } from '~/api/auth';
import {
  departmentsQueryOptions,
  type DirectoryEntry,
  directoryQueryOptions,
  peopleQueryOptions,
  type Person,
} from '~/api/people';
import { projectsQueryOptions } from '~/api/projects';
import { useServerClock } from '~/api/serverClock';
import { useOpenDm } from '~/api/teamChat';
import { listsQueryOptions } from '~/api/work';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { TaskSheet } from '~/features/work/TaskSheet';
import { calendarDate, formatMinutes, ROLE_LABEL } from '~/lib/format';
import { notify } from '~/lib/notify';
import { Avatar } from '~/ui/Avatar';
import { Bar } from '~/ui/Bar';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { Chips } from '~/ui/Chips';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Skeleton } from '~/ui/Skeleton';

interface Search {
  /** 'all', a department id, or 'project:<id>'. */
  group: string;
  q?: string;
  task?: string;
  run?: string;
  owner?: string;
}

export const Route = createFileRoute('/_app/team')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    group: typeof s['group'] === 'string' && s['group'] ? s['group'] : 'all',
    q: typeof s['q'] === 'string' && s['q'] ? s['q'] : undefined,
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
    owner: typeof s['owner'] === 'string' && s['owner'] ? s['owner'] : undefined,
  }),
  component: TeamPage,
});

/**
 * Everyone the caller can see, as cards: role, department, what they are on
 * right now, today's load, and the last 30 days. Filter by department or
 * project. Message opens a direct chat with them; Assign opens a new task with them as owner.
 */
function TeamPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/team' });
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const isManager = me.person.role !== 'member';
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const people = useQuery(peopleQueryOptions({ status: 'active' }));
  const departments = useQuery(departmentsQueryOptions);
  const projects = useQuery(projectsQueryOptions());
  // Everyone's name and face, readable by anyone: project members outside the caller's reach.
  const directory = useQuery(directoryQueryOptions);
  const lists = useQuery(listsQueryOptions);
  const todayBoard = useQuery({ ...todayAnalysisQueryOptions(), enabled: isManager });
  const load = useQuery({ ...availabilityQueryOptions({ range: 'day' }), enabled: isManager });
  const month = useQuery({ ...peopleAnalysisQueryOptions({}), enabled: isManager });

  const groupOptions = useMemo(() => {
    const opts: { value: string; label: string; count?: number }[] = [
      { value: 'all', label: 'All' },
    ];
    const list = people.data ?? [];
    for (const d of departments.data ?? []) {
      const n = list.filter((p) => p.departmentId === d.id).length;
      if (n > 0) opts.push({ value: d.id, label: d.name, count: n });
    }
    for (const p of (projects.data ?? []).filter(
      (x) => x.status !== 'archived' && x.status !== 'done',
    )) {
      opts.push({
        value: `project:${p.id}`,
        label: p.name,
        count: membersOf(p, people.data ?? [], directory.data ?? []).length,
      });
    }
    return opts;
  }, [people.data, departments.data, projects.data, directory.data]);

  const rows = useMemo(() => {
    let list: Member[];
    if (search.group.startsWith('project:')) {
      const project = (projects.data ?? []).find((p) => p.id === search.group.slice(8));
      list = project ? membersOf(project, people.data ?? [], directory.data ?? []) : [];
    } else {
      list = (people.data ?? []).map((p) => ({ ...p, inReach: true }));
      if (search.group !== 'all') list = list.filter((p) => p.departmentId === search.group);
    }
    list.sort((a, b) => a.name.localeCompare(b.name));
    const needle = (search.q ?? '').trim().toLowerCase();
    if (needle) list = list.filter((p) => p.name.toLowerCase().includes(needle));
    return list;
  }, [people.data, projects.data, directory.data, search.group, search.q]);

  const deptName = (id: string | null) =>
    (departments.data ?? []).find((d) => d.id === id)?.name ?? null;
  const stateOf = (id: string) => todayBoard.data?.people.find((p) => p.personId === id) ?? null;
  const loadOf = (id: string) => load.data?.find((p) => p.personId === id) ?? null;
  const monthOf = (id: string) => month.data?.people.find((p) => p.personId === id) ?? null;

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="People"
        title="Team"
        subtitle={
          isManager
            ? 'Who is on what, who has room, and how the month is going.'
            : 'The people you work with.'
        }
        actions={
          me.person.role === 'owner' ? (
            <Link
              to="/people"
              data-tour="team-manage"
              className="inline-flex h-10 items-center gap-2 rounded-control border border-line px-4 text-sm font-medium hover:bg-paper-2"
            >
              <Settings2 size={16} aria-hidden /> Manage people
            </Link>
          ) : undefined
        }
      />

      <div className="mb-5 flex flex-col gap-3">
        <div data-tour="team-filter">
          <Chips
            label="Group"
            options={groupOptions}
            value={search.group}
            onChange={(g) => go({ group: g })}
          />
        </div>
        <div className="max-w-xs" data-tour="team-search">
          <Input
            label="Search"
            type="search"
            placeholder="Search by name"
            value={search.q ?? ''}
            onChange={(e) => go({ q: e.target.value || undefined })}
          />
        </div>
      </div>

      {people.isPending ? (
        <Skeleton lines={6} />
      ) : people.isError ? (
        <ErrorNotice error={people.error} onRetry={() => people.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UserPlus aria-hidden />}
          title={search.q ? 'Nobody matches that search.' : 'Nobody in this group yet.'}
        />
      ) : (
        <ul
          key={search.group}
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
          data-tour="team-roster"
        >
          {rows.map((p, i) => (
            <li key={p.id}>
              <PersonCard
                person={p}
                you={p.id === me.person.id}
                department={deptName(p.departmentId)}
                state={stateOf(p.id)}
                load={loadOf(p.id)}
                month={monthOf(p.id)}
                canAssign={isManager && p.inReach}
                onAssign={() => go({ task: 'new', owner: p.id })}
                onOpenRun={(id) => go({ run: id })}
                delay={Math.min(i, 8) * 40}
              />
            </li>
          ))}
        </ul>
      )}

      <TaskSheet
        id={search.task ?? null}
        newOwnerId={search.owner}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined, owner: undefined })}
        onOpenRun={(id) => go({ task: undefined, run: id })}
        onOpenTask={(id) => go({ task: id, owner: undefined })}
      />
      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={(id) => go({ run: undefined, task: id })}
      />
    </div>
  );
}

/**
 * Everyone on a project. The people list holds only those in the caller's reach
 * (a manager's department, a member's own team), so the rest of the project
 * comes from the directory: shown, but without their work or an Assign.
 */
function membersOf(
  project: { members: { personId: string; role: 'lead' | 'member' }[] },
  people: Person[],
  directory: DirectoryEntry[],
): Member[] {
  const seen = new Map(people.map((p) => [p.id, p]));
  const named = new Map(directory.map((d) => [d.id, d]));
  const out: Member[] = [];
  for (const m of project.members) {
    const full = seen.get(m.personId);
    const entry = named.get(m.personId);
    if (full) out.push({ ...full, inReach: true, projectRole: m.role });
    // Not in the directory: disabled, so not on the team any more.
    else if (entry) {
      out.push({
        id: entry.id,
        name: entry.name,
        title: entry.title,
        departmentId: entry.departmentId,
        role: null,
        inReach: false,
        projectRole: m.role,
      });
    }
  }
  return out;
}

/** A card on the roster: a person in reach, or a project member known only by name. */
type Member = Pick<Person, 'id' | 'name' | 'title' | 'departmentId'> & {
  /** Null for someone outside the caller's reach: the directory does not say. */
  role: Person['role'] | null;
  /** In the caller's people list, so their day and month can be shown. */
  inReach: boolean;
  projectRole?: 'lead' | 'member';
};

type LoadRow = { scheduledMinutes: number; bookedMinutes: number; freeMinutes: number };
type MonthRow = { completed: number; onTimeRate: number | null; delays: number };

function PersonCard({
  person: p,
  you,
  department,
  state,
  load,
  month,
  canAssign,
  onAssign,
  onOpenRun,
  delay,
}: {
  person: Member;
  you: boolean;
  department: string | null;
  state: TodayPerson | null;
  load: LoadRow | null;
  month: MonthRow | null;
  canAssign: boolean;
  onAssign: () => void;
  onOpenRun: (id: string) => void;
  delay: number;
}) {
  const navigate = useNavigate();
  const openDm = useOpenDm();
  const message = () =>
    openDm.mutate(p.id, {
      onSuccess: (res) => void navigate({ to: '/chat/$id', params: { id: res.chat.id } }),
      onError: (e) => notify.error('Could not open the chat.', e),
    });
  const dot =
    state?.state === 'working' ? 'bg-done' : state?.state === 'idle' ? 'bg-warn' : 'bg-line';
  return (
    <Card delay={delay} className="flex h-full flex-col">
      <div className="flex items-start gap-3">
        <span className="relative">
          <Avatar name={p.name} size="lg" personId={p.id} viewable />
          {state && (
            <span
              aria-hidden
              className={`absolute right-0 bottom-0 size-3.5 rounded-full border-2 border-paper ${dot}`}
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">
            {p.name} {you && <span className="text-xs font-normal text-ink-3">(you)</span>}
          </p>
          <p className="truncate text-xs text-ink-3">
            {p.title ?? (p.role ? ROLE_LABEL[p.role] : 'Project member')}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.projectRole === 'lead' && <Pill tone="warn">Project lead</Pill>}
            {department && <Pill tone="accent">{department}</Pill>}
            {p.role && p.role !== 'member' && <Pill>{ROLE_LABEL[p.role]}</Pill>}
          </div>
        </div>
      </div>

      {state && (
        <p className="mt-3 text-xs text-ink-2">
          {state.state === 'working' && state.activeOccurrenceId ? (
            <>
              Working on{' '}
              <button
                type="button"
                onClick={() => onOpenRun(state.activeOccurrenceId!)}
                className="font-medium text-accent hover:underline"
              >
                {state.activeTaskTitle}
              </button>
            </>
          ) : state.state === 'idle' ? (
            'In today, nothing running'
          ) : (
            'Off today'
          )}
        </p>
      )}

      {(state || month) && (
        <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-panel bg-paper-2/60 p-2">
            <dt className="text-[11px] text-ink-3">Open today</dt>
            <dd className="font-display text-lg font-semibold tabular">{state?.todayOpen ?? 0}</dd>
          </div>
          <div className="rounded-panel bg-paper-2/60 p-2">
            <dt className="text-[11px] text-ink-3">Done, 30 d</dt>
            <dd className="font-display text-lg font-semibold tabular">{month?.completed ?? 0}</dd>
          </div>
          <div className="rounded-panel bg-paper-2/60 p-2">
            <dt className="text-[11px] text-ink-3">On time</dt>
            <dd className="font-display text-lg font-semibold tabular">
              {month?.onTimeRate === null || month?.onTimeRate === undefined
                ? '—'
                : `${Math.round(month.onTimeRate * 100)}%`}
            </dd>
          </div>
        </dl>
      )}

      {load && (
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[11px] text-ink-3">
            <span>Today's load</span>
            <span className="tabular">
              {formatMinutes(load.bookedMinutes)} of {formatMinutes(load.scheduledMinutes)}
            </span>
          </div>
          <Bar
            value={load.bookedMinutes}
            max={Math.max(load.scheduledMinutes, 1)}
            tone={load.bookedMinutes > load.scheduledMinutes ? 'late' : 'accent'}
          />
        </div>
      )}

      <div className="mt-auto flex gap-2 pt-4">
        <Button
          variant="secondary"
          size="sm"
          icon={<MessageSquare size={14} aria-hidden />}
          onClick={message}
          disabled={openDm.isPending}
          className="flex-1"
        >
          Message
        </Button>
        {canAssign && (
          <Button
            size="sm"
            icon={<Plus size={14} aria-hidden />}
            onClick={onAssign}
            className="flex-1"
          >
            Assign a task
          </Button>
        )}
      </div>
    </Card>
  );
}
